import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { RichText } from "~/features/ui/rich-text";
import { takeFirst } from "~/features/app/utils";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const fragment = graphql(`
  fragment HighlightsHighlight on blocks_highlightsHighlight_BlockType {
    title: baseText
    text: baseRichText
    link: baseLink {
      ...Link
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  count?: string;
  horizontal?: boolean;
};
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type HighlightsHighlightViewProps = {
  count?: string;
  horizontal?: boolean;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  text?: React.ReactNode;
  title?: string | null;
};
export function HighlightsHighlightView({
  count,
  horizontal,
  link,
  text,
  title
}: HighlightsHighlightViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  const header = count ? <_StyledDiv>
      <_StyledDiv2>
        <_StyledSpan>
          {count}
        </_StyledSpan>
        {title && <_StyledH>{title}</_StyledH>}
      </_StyledDiv2>
      <_StyledSpan2 />
    </_StyledDiv> : <_StyledDiv3>
      {title && <_StyledH2>{title}</_StyledH2>}
      <_StyledSpan3 />
    </_StyledDiv3>;
  return <_StyledDiv4 $_css={[{
    "display": "flex",
    "flexDirection": "column",
    "gap": "var(--spacing-sm)",
    "paddingTop": "var(--spacing-lg)",
    "paddingBottom": "var(--spacing-lg)"
  }, horizontal && {
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "justifyContent": "space-between",
    "rowGap": "var(--spacing-lg)"
  }]}>
      {header}
      {text}
      {isLinkValid(cta) && <_StyledDiv5 $_css2={[{
      "marginTop": "var(--spacing-xl)"
    }, horizontal && {
      "marginTop": "auto"
    }]}>
          <Button as={Link} variant="underline" {...cta} icon={icon} />
        </_StyledDiv5>}
    </_StyledDiv4>;
}
export function HighlightsHighlight(props: Props) {
  const {
    count,
    horizontal,
    ...rest
  } = props;
  const {
    title,
    text,
    link
  } = useFragment(fragment, rest);
  return <HighlightsHighlightView count={count} horizontal={horizontal} link={link as HighlightsHighlightViewProps["link"]} text={text ? <_StyledRichText text={text} /> : undefined} title={title} />;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-4uptr4-0"
})({
  "display": "flex",
  "alignItems": "flex-start",
  "gap": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "alignItems": "center"
  }
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-4uptr4-1"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "var(--spacing-sm)",
  "@media (min-width: 1024px)": {
    "flexDirection": "row",
    "alignItems": "flex-start",
    "gap": "var(--spacing-lg)"
  }
});
var _StyledSpan = _styled3("span").withConfig({
  componentId: "sc-sf-4uptr4-2"
})({
  "marginTop": "var(--spacing-sm)",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "color": "var(--text-transparent)",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledH = _styled4("h3").withConfig({
  componentId: "sc-sf-4uptr4-3"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledSpan2 = _styled5("span").withConfig({
  componentId: "sc-sf-4uptr4-4"
})({
  "marginLeft": "auto",
  "marginRight": "var(--spacing-sm)",
  "height": "0.8rem",
  "width": "0.8rem",
  "flexShrink": "0",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
});
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-4uptr4-5"
})({
  "display": "flex",
  "gap": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "gap": "var(--spacing-lg)"
  }
});
var _StyledH2 = _styled7("h3").withConfig({
  componentId: "sc-sf-4uptr4-6"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledSpan3 = _styled8("span").withConfig({
  componentId: "sc-sf-4uptr4-7"
})({
  "marginLeft": "auto",
  "marginTop": "0.8rem",
  "height": "0.8rem",
  "width": "0.8rem",
  "flexShrink": "0",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
});
var _StyledDiv4 = _styled9("div").withConfig({
  componentId: "sc-sf-4uptr4-8"
})`${p => p.$_css}`;
var _StyledDiv5 = _styled10("div").withConfig({
  componentId: "sc-sf-4uptr4-9"
})`${p => p.$_css2}`;
var _StyledRichText = /*#__PURE__*/_styled11(RichText).withConfig({
  displayName: "highlights-highlight___StyledRichText",
  componentId: "sc-xi9hig-0"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});